import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './SideNav.module.css';
import { UserRoleString } from '../../../enums/userRole';
import { useEffect, useState } from 'react';
import { LogOut, PanelLeftClose, PanelLeftOpen, ChevronDown, ChevronUp } from 'lucide-react';
import logoIcon from '../../../assets/logo_icon.png';

type NavItem = {
  name: string;
  href: string;
  icon?: React.ComponentType<{ size?: string | number }>; 
}

export type NavItems = {
  name: string;
  href: string;
  icon: React.ComponentType<{ size?: string | number }>; 
  subItems?: Array<NavItem>;
};

interface SideNavProps {
  items: NavItems[];
  logoSrc: string;
  user: any | null;
  onLogout: () => void | Promise<void>;
  mobileOpen?: boolean;
  onMobileToggle?: () => void;
}

export default function SideNav({ items, logoSrc, user, onLogout, mobileOpen: mobileOpenProp, onMobileToggle }: SideNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [openItemName, setOpenItemName] = useState<string | null>(null);
  const [mobileOpenInternal, setMobileOpenInternal] = useState(false);
  const mobileOpen = mobileOpenProp ?? mobileOpenInternal;
  const setMobileOpen = (val: boolean) => {
    if (onMobileToggle) {
      onMobileToggle();
    } else {
      setMobileOpenInternal(val);
    }
  };
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in e ? e.matches : (e as MediaQueryList).matches;
      setIsMobile(matches);
    };
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler as any);
  }, []);

  const toggleCollapsed = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
      return;
    }
    setCollapsed((c) => !c);
  };

  const isParentActive = (parentHref: string, subItems?: NavItem[]) => {
    if (location.pathname === parentHref) return true;
    return subItems?.some((sub) => isSubActive(sub)) || false;
  };

  const isSubActive = (sub: NavItem) => {
    try {
      const subUrl = new URL(sub.href, window.location.origin);
      const currentParams = new URLSearchParams(location.search);
      // Must match path first
      if (location.pathname !== subUrl.pathname) return false;
      // If sub link has params, ensure all of them match
      const entries = Array.from(subUrl.searchParams.entries());
      for (const [key, value] of entries) {
        if (currentParams.get(key) !== value) return false;
      }
      // If no params in sub link, path match is sufficient
      return true;
    } catch {
      // Fallback: compare href without query
      const hrefPath = sub.href.split('?')[0];
      return location.pathname === hrefPath;
    }
  };

  const activeParentName = items.find((item) => {
    const hasSubItems = Array.isArray(item.subItems) && item.subItems.length > 0;
    return hasSubItems && isParentActive(item.href, item.subItems);
  })?.name ?? null;

  useEffect(() => {
    if (activeParentName) {
      setOpenItemName(activeParentName);
    }
  }, [activeParentName]);

  return (
    <>
    {isMobile && mobileOpen ? <div className={styles.overlay} onClick={() => setMobileOpen(false)} /> : null}
    <aside className={`${styles.sideNav} ${collapsed ? styles.collapsed : ''} ${isMobile && mobileOpen ? styles.mobileOpen : ''}`}>
      <div className={styles.navLinks}>
        <span className={styles.expandToggle} onClick={toggleCollapsed} title={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
        </span>
        <div className={styles.brand} onClick={() => navigate('/dashboard')}>
          <img src={collapsed ? logoIcon : logoSrc} alt="Oitools Studio Logo" className={styles.logo} />
          <h1>Studio</h1>

        </div>
        <nav>
          {items.map((item) => {
            const hasSub = Array.isArray(item.subItems) && item.subItems.length > 0;
            const parentActive = isParentActive(item.href, item.subItems);

            if (!hasSub) {
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${styles.navLink} ${location.pathname === item.href ? styles.navLinkActive : ''}`}
                  title={collapsed ? item.name : undefined}
                  onClick={() => {
                    setOpenItemName(null);
                    if (isMobile) setMobileOpen(false);
                  }}
                >
                  <item.icon size={16} />
                  <span className={styles.label}>{item.name}</span>
                </Link>
              );
            }

            const isOpen = openItemName === item.name;
            const openSubMenu = () => setOpenItemName(item.name);
            const toggleSub = () => setOpenItemName((current) => current === item.name ? null : item.name);
            return (
              <div key={item.name}>
                <div className={`${styles.navItemRow}`}>
                  <Link
                    to={item.href}
                    className={`${styles.navLink} ${parentActive ? styles.navLinkActive : ''}`}
                    title={collapsed ? item.name : undefined}
                    onClick={() => {
                      openSubMenu();
                      if (isMobile) setMobileOpen(false);
                    }}
                  >
                    {item.icon ? <item.icon size={16} /> : null}
                    <span className={styles.label}>{item.name}</span>
                  </Link>
                  {!collapsed && (
                    <span className={styles.expandIcon} onClick={toggleSub} title={isOpen ? 'Collapse' : 'Expand'}>
                      {isOpen ? <ChevronUp size={14} color='white'/> : <ChevronDown size={14} color='white'/>}
                    </span>
                  )}
                </div>
                {!collapsed && item.subItems && (
                  <div
                    className={`${styles.subMenu} ${isOpen ? styles.subMenuOpen : styles.subMenuClosed}`}
                    aria-hidden={!isOpen}
                  >
                    {item.subItems.map((sub: any) => (
                      <Link
                        key={sub.href || sub.name}
                        to={sub.href}
                        className={`${styles.subItem} ${isSubActive(sub) ? styles.subNavLinkActive : ''}`}
                        onClick={() => { if (isMobile) setMobileOpen(false); }}
                      >
                        {sub.icon ? <sub.icon size={14} /> : null}
                        <span className={styles.label}>{sub.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      <div className={styles.userSection} >
        {user && (
          <div className={styles.userInfo}>
            <div className={styles.userAvatar} onClick={()=> setCollapsed(false)}>
              {user?.first_name?.slice(0, 1)?.toUpperCase()}
              {user?.last_name?.slice(0, 1)?.toUpperCase()}
            </div>
            <div className={styles.userDetails}>
              <span className={styles.userName}>
                {user?.first_name} {user?.last_name}
              </span>
              <span className={styles.userRole}>
                {user?.role !== undefined ? UserRoleString[user?.role?.id || 4] : ''}
              </span>
            </div>
          </div>
        )}

        <span
          onClick={onLogout}
          className={styles.logoutBtn}
          title="Log Off"
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {collapsed ? null : <LogOut color="red" size={16} />}
        </span>
      </div>
      {!collapsed && <span className={styles.versionLabel}>v{__APP_VERSION__}</span>}
    </aside>
    </>
  );
}
